import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Bold, Italic, Underline, Link2, List, ListOrdered, Heading2, Pilcrow, Eraser } from 'lucide-react';

/**
 * Lightweight WYSIWYG editor built on contentEditable + document.execCommand.
 * No dependency, ~10KB, perfect fit for the admin broadcast composer.
 *
 * Props:
 *   value     - current HTML string
 *   onChange  - (html) => void
 *   placeholder - shown when the editor is empty
 *   testId    - data-testid for the editor surface
 *
 * The editor emits clean HTML (paragraphs / line-breaks / bold / italic /
 * underline / lists / anchors / headings) — exactly what the existing
 * Mailgun template supports, so no backend changes are needed.
 */
export function RichTextEditor({ value, onChange, placeholder, testId }) {
  const editorRef = useRef(null);
  const isInternalUpdate = useRef(false);
  const [activeStates, setActiveStates] = useState({});

  // Sync external value → editor when the parent resets it (e.g. after send).
  useEffect(() => {
    if (!editorRef.current) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if ((editorRef.current.innerHTML || '') !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const refreshState = useCallback(() => {
    if (!document.queryCommandState) return;
    try {
      setActiveStates({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        ul: document.queryCommandState('insertUnorderedList'),
        ol: document.queryCommandState('insertOrderedList'),
      });
    } catch (e) { /* iOS sometimes throws */ }
  }, []);

  const fireChange = useCallback(() => {
    if (!editorRef.current || !onChange) return;
    isInternalUpdate.current = true;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = useCallback((cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    refreshState();
    fireChange();
  }, [fireChange, refreshState]);

  const insertLink = () => {
    const sel = window.getSelection();
    const selectedText = sel ? sel.toString() : '';
    const url = window.prompt('Link URL:', 'https://');
    if (!url) return;
    if (selectedText) {
      exec('createLink', url);
    } else {
      const safe = url.replace(/"/g, '&quot;');
      // Insert the URL as both text and link.
      exec('insertHTML', `<a href="${safe}" target="_blank" rel="noopener">${safe}</a>`);
    }
  };

  // Paste plain text instead of fully-styled HTML — keeps the editor clean and
  // avoids the user's clipboard fonts/colors leaking into the branded template.
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData)?.getData('text/plain') || '';
    // Convert newlines to <br>, double newlines to paragraph breaks.
    const html = text
      .split(/\n\n+/)
      .map(par => par.replace(/\n/g, '<br>'))
      .join('</p><p>');
    document.execCommand('insertHTML', false, `<p>${html}</p>`);
    fireChange();
  };

  const isEmpty = !value || /^(<p>(<br\/?>)?<\/p>|<br\/?>|\s)*$/i.test(value);

  const ToolButton = ({ onClick, active, title, tid, children }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      data-testid={tid}
      className={`w-8 h-8 inline-flex items-center justify-center rounded transition-colors
        ${active ? 'bg-[#00247D] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden focus-within:border-[#00247D] focus-within:ring-1 focus-within:ring-[#00247D]/30">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 bg-slate-50/60"
        data-testid={`${testId || 'rich-editor'}-toolbar`}
      >
        <ToolButton onClick={() => exec('bold')} active={activeStates.bold} title="Bold (Ctrl/Cmd+B)" tid="rte-bold">
          <Bold className="w-4 h-4" />
        </ToolButton>
        <ToolButton onClick={() => exec('italic')} active={activeStates.italic} title="Italic (Ctrl/Cmd+I)" tid="rte-italic">
          <Italic className="w-4 h-4" />
        </ToolButton>
        <ToolButton onClick={() => exec('underline')} active={activeStates.underline} title="Underline (Ctrl/Cmd+U)" tid="rte-underline">
          <Underline className="w-4 h-4" />
        </ToolButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolButton onClick={() => exec('formatBlock', 'H2')} title="Heading" tid="rte-h2">
          <Heading2 className="w-4 h-4" />
        </ToolButton>
        <ToolButton onClick={() => exec('formatBlock', 'P')} title="Paragraph" tid="rte-p">
          <Pilcrow className="w-4 h-4" />
        </ToolButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolButton onClick={() => exec('insertUnorderedList')} active={activeStates.ul} title="Bulleted list" tid="rte-ul">
          <List className="w-4 h-4" />
        </ToolButton>
        <ToolButton onClick={() => exec('insertOrderedList')} active={activeStates.ol} title="Numbered list" tid="rte-ol">
          <ListOrdered className="w-4 h-4" />
        </ToolButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolButton onClick={insertLink} title="Insert link" tid="rte-link">
          <Link2 className="w-4 h-4" />
        </ToolButton>
        <ToolButton onClick={() => exec('removeFormat')} title="Clear formatting" tid="rte-clear">
          <Eraser className="w-4 h-4" />
        </ToolButton>
      </div>

      {/* Editor surface */}
      <div className="relative">
        {isEmpty && placeholder && (
          <div className="absolute top-2 left-3 text-slate-400 pointer-events-none text-sm leading-relaxed">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={fireChange}
          onKeyUp={refreshState}
          onMouseUp={refreshState}
          onPaste={handlePaste}
          onFocus={refreshState}
          data-testid={testId || 'rich-editor-surface'}
          className="prose prose-sm max-w-none px-3 py-2 min-h-[220px] outline-none text-sm leading-relaxed text-slate-800 [&_a]:text-[#00247D] [&_a]:underline [&_h2]:text-[#00247D] [&_h2]:font-bold [&_h2]:text-base [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        />
      </div>
    </div>
  );
}
